import React, { useState } from 'react';
import { X, Zap, Target, TrendingUp, Award } from 'lucide-react';

/**
 * HowToPlay Component - Tutorial Modal
 *
 * Displays game instructions and tips in a modal overlay.
 * Can be easily modified or disabled without affecting other features.
 */
export default function HowToPlay({ isOpen, onClose }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-gray-900 to-purple-900 border border-purple-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-purple-500/30 p-6 flex items-center justify-between">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                        How to Play MINER
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Step 1 */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="bg-blue-500/20 p-3 rounded-lg">
                                <Zap className="text-blue-400" size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2 text-blue-400">1. Connect Your Wallet</h3>
                                <p className="text-gray-300">
                                    Click "Connect Wallet" and select your Solana wallet (Phantom, Solflare, etc.).
                                    Make sure you have some SOL for mining.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="bg-purple-500/20 p-3 rounded-lg">
                                <Target className="text-purple-400" size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2 text-purple-400">2. Select Blocks</h3>
                                <p className="text-gray-300 mb-3">
                                    Choose one or more blocks on the 5x5 grid (25 blocks total).
                                    The more blocks you select, the higher your chance of winning!
                                </p>
                                <ul className="text-sm text-gray-400 space-y-1">
                                    <li>• Each round, one winning block is randomly selected</li>
                                    <li>• Select multiple blocks to increase your odds</li>
                                    <li>• You can deploy different amounts per block</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Step 3 */}
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="bg-orange-500/20 p-3 rounded-lg">
                                <TrendingUp className="text-orange-400" size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2 text-orange-400">3. Deploy SOL</h3>
                                <p className="text-gray-300 mb-3">
                                    Choose how much SOL to deploy per block and for how many rounds.
                                    Click "Deploy SOL" to start mining!
                                </p>
                                <div className="bg-orange-500/10 rounded-lg p-3 text-sm">
                                    <strong>Example:</strong> 0.1 SOL × 5 blocks × 60 rounds = 30 SOL total
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 4 */}
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="bg-green-500/20 p-3 rounded-lg">
                                <Award className="text-green-400" size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2 text-green-400">4. Win Rewards</h3>
                                <p className="text-gray-300 mb-3">
                                    Every 60 seconds, one random block wins. If you deployed on the winning block:
                                </p>
                                <ul className="text-sm text-gray-300 space-y-2">
                                    <li>✅ <strong>Get your SOL back</strong> plus share of losing blocks' SOL</li>
                                    <li>✅ <strong>Earn MINER tokens</strong> (1 MINER per round split among winners)</li>
                                    <li>✅ <strong>1 in 625 chance</strong> to hit the Motherlode jackpot!</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Features */}
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                        <h3 className="text-xl font-bold mb-4 text-yellow-400">💎 Special Features</h3>
                        <div className="space-y-3 text-sm text-gray-300">
                            <div>
                                <strong className="text-yellow-400">Motherlode Jackpot:</strong>
                                <p>1 in 625 chance each round to win the growing jackpot! Grows by 0.2 MINER every round.</p>
                            </div>
                            <div>
                                <strong className="text-green-400">Unrefined MINER:</strong>
                                <p>Claim your earned MINER with a 10% refining fee. Fee goes to the refined pool.</p>
                            </div>
                            <div>
                                <strong className="text-blue-400">Refined MINER:</strong>
                                <p>Earn refined MINER from other players' claiming fees - completely free!</p>
                            </div>
                        </div>
                    </div>

                    {/* Tips */}
                    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                        <h3 className="text-lg font-bold mb-3">💡 Pro Tips</h3>
                        <ul className="text-sm text-gray-300 space-y-2">
                            <li>• Start with small amounts to test the game</li>
                            <li>• More blocks = higher win chance but lower individual rewards</li>
                            <li>• Watch the round timer - new winner every 60 seconds</li>
                            <li>• Check your win rate and ROI in the stats panel</li>
                            <li>• Auto-Miner will automatically re-deploy your blocks</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-900/95 backdrop-blur-sm border-t border-purple-500/30 p-6">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 rounded-lg font-bold transition-all"
                    >
                        Let's Mine!
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * HowToPlayButton - Button to open the How to Play modal
 */
export function HowToPlayButton() {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
            >
                How to Play
            </button>
            <HowToPlay isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </>
    );
}
